import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { HashLink as Link } from "react-router-hash-link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Breadcrumbs, Container, Stack, Typography } from "@mui/material";
import Optionals from "./Optionals";
import ChooseVehicle from "./ChooseVehicle";
import ClientForm from "./ClientForm";
import Payment from "./Payment";
import Confirmation from "./Confirmation";
import dayjs from "dayjs";
import { EXTRAS, DESK_LABELS } from "../helpers/Constants";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { pushToDataLayer } from "../helpers/pushToDataLayer";

const theme = createTheme({
  palette: {
    primary: {
      main: "#263677",
    },
    secondary: {
      main: "#EE8013",
    },
    danger: "#fa0505",
  },
});

function ReservationCheckout() {
  const { t } = useTranslation();
  const reservationData = useLocation();
  const reservationInfo = reservationData.state;
  const [avaiabilities, setVehicleAvaiabilities] = useState(
    reservationInfo?.avaiabilities,
  );
  const [reservationDetails, setReservationDetails] = useState(
    reservationInfo?.reservationDetails,
  );
  const [wasCarChoosed, setWasCarChoosed] = useState(false);
  const [wasOptionalsChoosed, setWasOptionalsChoosed] = useState(false);
  const [wasClientDetailsFiled, setWasClientDetailsFiled] = useState(false);
  const [vehicleChoosedDetails, setVehicleChoosedDetails] = useState(false);
  const [vehicleExtras, setVehicleExtras] = useState(EXTRAS);
  const [loadingPage, setLoadingPage] = useState(false);
  const [clientDetails, setClientDetails] = useState(null);
  const [reservationFinalData, setReservationFinalData] = useState(null);
  const [paymentAproved, setPaymentAproved] = useState(false);
  const [paypalOrderId, setPaypalOrderId] = useState("");
  const [checkInData, setCheckInData] = useState("");
  const [reservationConfirmed, setReservationConfirmed] = useState(false);
  const [totalDays, setTotalDays] = useState(0);
  const [totalRentalValue, setTotalRentalValue] = useState(
    (
      Math.round(vehicleChoosedDetails?.totalPrice * 100) /
      100 /
      (vehicleChoosedDetails?.updatedTotalDays === 0
        ? totalDays
        : vehicleChoosedDetails?.updatedTotalDays)
    ).toFixed(2),
  );
  const [pageChangedFromOptional, setPageChangedFromOptional] = useState(false);
  const [youngDriverClient, setYoungDriverClient] = useState(false);
  const [pickUpDesk, setPickUpDesk] = useState(reservationInfo?.pickUpDesk);
  const [dropOffDesk, setDropOffDesk] = useState(reservationInfo?.dropOffDesk);
  const [paymentType, setPaymentType] = useState("full");
  const submittedOrderRef = useRef(null);
  const { step } = useParams(); // Pega o valor de :step do URL
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (paymentAproved) {
      setLoadingPage(true);
      const itemName = "itemName";
      const itemPrice = "itemPrice";
      const itemNameKey = `${itemName}[${vehicleChoosedDetails.vehicleCode}]`;
      const itemPriceKey = `${itemPrice}[${vehicleChoosedDetails.vehicleCode}]`;

      const equipment = {};

      vehicleExtras?.extraEquipments?.forEach((item) => {
        const { Equipment, Charge } = item;
        const { Description, EquipType } = Equipment;
        const { Amount, IncludedInRate } = Charge;
        const itemName = `itemName[${EquipType}]`;
        const itemPrice = `itemPrice[${EquipType}]`;
        const pricedEquips = `pricedEquips[${EquipType}]`;

        equipment[itemName] = Description;
        equipment[itemPrice] = Amount;
        equipment[pricedEquips] = IncludedInRate;
      });

      const coverages = {};
      vehicleExtras?.extraCoverages?.forEach((item) => {
        const { Coverage, Charge } = item;
        const { CoverageType } = Coverage;
        const { IncludedInRate } = Charge;
        const coverage = `coverages[${CoverageType}]`;
        coverages[coverage] = IncludedInRate;
      });

      const data = {
        ...reservationDetails,
        loc_pickup: pickUpDesk,
        loc_return: dropOffDesk,
        SIPP: vehicleChoosedDetails.vehicleCode,
        [itemNameKey]: vehicleChoosedDetails.vehicleModel,
        [itemPriceKey]: vehicleChoosedDetails.totalPrice,
        ...clientDetails,
      };

      setReservationFinalData(data);

      submitData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasClientDetailsFiled, paymentAproved]);

  const submitData = async (data) => {
    if (submittedOrderRef.current === paypalOrderId) return;
    submittedOrderRef.current = paypalOrderId;

    try {
      const response = await fetch(
        "https://www.whynotcarrental.com/Backend/reservationSub.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        throw new Error(`Reservation submission failed: ${response.status}`);
      }

      if (response.ok) {
        const reservationSubmited = await response.json();

        const checkData = {
          idreserva:
            reservationSubmited.VehResRSCore.VehReservation.VehSegmentCore
              .ConfID.ID,
          customer_name:
            reservationSubmited.VehResRSCore.VehReservation.Customer.Primary
              .PersonName.GivenName,
          approvalCode: paypalOrderId,
          amount: totalRentalValue.toFixed(2),
          rph: "PayPal",
        };
        setCheckInData(checkData);

        try {
          const response = await fetch(
            "https://www.whynotcarrental.com/Backend/reservationCheckIn.php",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(checkData),
            },
          );

          if (response.ok) {
            await response.json();
          }
        } catch (error) {
          console.error("Check-in registration failed:", error);
        }

        let emailExtras = "";

        vehicleExtras
          ?.filter((fee) => fee.quantity > 0)
          ?.map(
            (fee) =>
              (emailExtras += `\n${
                fee.extraName === "fullInsurance"
                  ? t("fullInsurance")
                  : fee.extraName === "babySeat"
                    ? t("babySeat")
                    : fee.extraName === "babySeatIsofix"
                      ? t("babySeatIsofix")
                      : fee.extraName === "boosterSeat"
                        ? t("boosterSeat")
                        : fee.extraName === "additionalDriver"
                          ? t("additionalDriver")
                          : fee.extraName === "youngDriver"
                            ? t("youngDriver")
                            : fee.extraName === "airportDeliveryExtra"
                              ? t("airportDeliveryExtra")
                              : fee.extraName === "outOfFunchalDelivery"
                                ? t("outOfFunchalDelivery")
                                : null
              } -> ${fee.totalValue}€, \n`),
          );

        const emailData = {
          bookingConfirmed: t("bookingConfirmed"),
          bookingSummary: t("bookingSummary"),
          idreserva: { value: checkData.idreserva, label: t("orderId") },
          paymentType:
            paymentType === "partial"
              ? t("partialValuePaid")
              : t("totalValuePaid"),
          payed: t("payed"),
          totalRentalValue: totalRentalValue.toFixed(2),
          fullName: {
            value: clientDetails.customer_name,
            label: t("fullName"),
          },
          email: { value: clientDetails.customer_email, label: t("email") },
          model: {
            value: vehicleChoosedDetails.vehicleModel,
            label: t("model"),
          },
          group: {
            value: vehicleChoosedDetails.vehicleCode,
            label: t("group"),
          },
          pickupPlace: {
            value: DESK_LABELS[pickUpDesk] ?? "Unknown Location",
            label: t("pickUp"),
          },
          returnPlace: {
            value: DESK_LABELS[dropOffDesk] ?? "Unknown Location",
            label: t("dropOff"),
          },
          pickupDate: {
            value: reservationDetails.date_pickup,
            label: t("deliveryDate"),
          },
          pickupTime: {
            value: reservationDetails.time_pickup,
            label: t("deliveryTime"),
          },
          returnDate: {
            value: reservationDetails.date_return,
            label: t("returnDate"),
          },
          returnTime: {
            value: reservationDetails.time_return,
            label: t("returnTime"),
          },
          totalDays: { value: totalDays, label: t("Days") },
          extras: { value: emailExtras, label: t("extrasChoosed") },
          phoneNumber: {
            value: clientDetails.customer_telephone,
            label: t("phoneNumber"),
          },
          countryCode: {
            value: clientDetails.countryCode,
            label: t("countryCode"),
          },
          location: {
            value: clientDetails.loc_flight,
            label: t("location"),
          },
          instructions: t("instructionsTitle"),
          instructionsInfo:
            pickUpDesk === 41
              ? t("funchalInstructions")
              : pickUpDesk === 42
                ? t("airportInstructions")
                : t("hotelInstructions"),
          furtherQuestions: t("furtherQuestions"),
          deskLocation: pickUpDesk,
          orSimilar: t("orSimilar"),
          totalRentalValueInfo: t("totalRentalValueInfo"),
          bestRegards: t("bestRegards"),
          hello: t("hello"),
        };

        const sendEmailConfirmation = async () => {
          try {
            const response = await fetch(
              "https://app.autocrescente.com/api/email_confirmation",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(emailData),
              },
            );

            const result = await response.json();

            if (response.ok) {
              console.log("Email enviado:", result.message);
              setLoadingPage(false);
            } else {
              console.error("Erro no envio:", result);
              setLoadingPage(false);
              toast.error(
                "Your booking is confirmed but we could not send the confirmation email. Please contact us.",
                { duration: 8000 }
              );
            }
          } catch (error) {
            console.error("Erro na requisição:", error);
            setLoadingPage(false);
            toast.error(
              "Your booking is confirmed but we could not send the confirmation email. Please contact us.",
              { duration: 8000 }
            );
          }
        };

        const reservationLogs = async () => {
          try {
            const response = await fetch(
              "https://www.whynotcarrental.com/Backend/reservationLogs.php",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(emailData),
              },
            );

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(data.message);
          } catch (error) {
            console.error("Error saving booking:", error);
          }
        };

        reservationLogs();
        sendEmailConfirmation();

        const extrasItems =
          vehicleExtras
            ?.filter((fee) => fee.quantity > 0)
            ?.map((fee) => ({
              item_name: fee.extraName,
              price: fee.totalValue,
              quantity: fee.quantity,
            })) || [];

        console.log("DATA LAYER EVENT:", {
          event: "purchase",
          transaction_id: checkData.idreserva,
        });

        pushToDataLayer({
          event: "purchase",
          debug_mode: true,
          ecommerce: {
            transaction_id: checkData.idreserva,
            value: parseFloat(totalRentalValue),
            currency: "EUR",
            items: [
              {
                item_name: vehicleChoosedDetails.vehicleModel,
                item_id: vehicleChoosedDetails.vehicleCode,
                item_category: "car_rental",
                price: parseFloat(totalRentalValue),
                quantity: 1,
              },
              ...extrasItems,
            ],
          },
        });

        setReservationConfirmed(true);
      }
    } catch (error) {
      console.error("Reservation submission error:", error);
      setLoadingPage(false);
      toast.error(
        "Something went wrong while processing your reservation. Your payment was captured — please contact us with your PayPal order ID.",
        { duration: 12000 }
      );
    }
  };

  const recalculateExtras = (
    currentExtras,
    pickUpDesk,
    dropOffDesk,
    youngDriverClient,
    totalDays,
    vehicleChoosedDetails,
  ) => {
    const extrasArray = Array.isArray(currentExtras)
      ? currentExtras.map((extra) => ({ ...extra }))
      : [];

    const basePrice = parseFloat(vehicleChoosedDetails?.totalPrice || 0);

    if (!extrasArray.length) {
      return {
        extras: [],
        totalRentalValue: basePrice,
      };
    }

    // Handle Young Driver Extra
    const youngDriverExtra = extrasArray.find((extra) => extra.id === 3);
    if (youngDriverExtra) {
      youngDriverExtra.quantity = youngDriverClient ? 1 : 0;
    }

    // Calculate total value for each extra
    extrasArray.forEach((extra) => {
      const quantity = extra.quantity || 0;
      const value = extra.value || 0;
      const days = totalDays || 1;
      const maxValue = extra.maxValue ?? Infinity;

      const total = extra.totalByDay
        ? quantity * value * days
        : quantity * value;

      extra.totalValue = Math.min(total, maxValue);
    });

    const airportExtra = extrasArray.find((extra) => extra.id === 10);
    const outOfFunchalExtra = extrasArray.find((extra) => extra.id === 9);

    if (airportExtra) airportExtra.quantity = 0;
    if (outOfFunchalExtra) outOfFunchalExtra.quantity = 0;

    if (pickUpDesk === 42 || dropOffDesk === 42) {
      if (airportExtra) airportExtra.quantity = 1;
    }
    if (pickUpDesk === 46 || pickUpDesk === 47) {
      if (outOfFunchalExtra) outOfFunchalExtra.quantity += 1;
    }
    if (dropOffDesk === 46 || dropOffDesk === 47) {
      if (outOfFunchalExtra) outOfFunchalExtra.quantity += 1;
    }

    // Sum up all extras
    const totalExtras = extrasArray.reduce(
      (acc, extra) => acc + extra.totalValue,
      0,
    );

    // Final total
    const total = basePrice + totalExtras;

    return {
      extras: extrasArray,
      totalRentalValue: total,
    };
  };

  useEffect(() => {
    const { extras, totalRentalValue } = recalculateExtras(
      vehicleExtras,
      pickUpDesk,
      dropOffDesk,
      youngDriverClient,
      totalDays,
      vehicleChoosedDetails,
    );

    setVehicleExtras(extras);
    setTotalRentalValue(totalRentalValue);
  }, [
    JSON.stringify(vehicleExtras), // safely compare the deep object
    pickUpDesk,
    dropOffDesk,
    youngDriverClient,
    totalDays,
    vehicleChoosedDetails,
  ]);

  useEffect(() => {
    function calculateTotalDays(
      pickUpDate,
      pickUpTime,
      returnDate,
      returnTime,
    ) {
      const pickUpDateTime = dayjs(`${pickUpDate} ${pickUpTime}`);
      const returnDateTime = dayjs(`${returnDate} ${returnTime}`);

      // Calculate the difference in total minutes
      const diffInMinutes = returnDateTime.diff(pickUpDateTime, "minute");

      // Calculate total days by dividing by minutes in a day, rounding up
      const updatedDays = Math.ceil(diffInMinutes / 1440);

      setTotalDays(updatedDays);
    }
    calculateTotalDays(
      reservationDetails?.date_pickup,
      reservationDetails?.time_pickup,
      reservationDetails?.date_return,
      reservationDetails?.time_return,
    );
  }, [
    reservationDetails?.date_pickup,
    reservationDetails?.date_return,
    reservationDetails?.time_pickup,
    reservationDetails?.time_return,
    wasCarChoosed,
  ]);

  const myVehiclePageHandler = () => {
    if (wasCarChoosed) {
      setPageChangedFromOptional(true);
      setWasCarChoosed(false);
    }
    setWasCarChoosed(false);
  };

  useEffect(() => {
    // Se o URL for apenas /reservationCheckout, força a ida para /veiculo
    if (!step) {
      navigate("/reservationCheckout/vehicle", {
        replace: true,
        state: reservationInfo,
      });
    }
  }, [step, navigate, reservationInfo]);

  // Função para mudar de passo e atualizar o URL
  const goToStep = (nextStep) => {
    navigate(`/reservationCheckout/${nextStep}`, {
      state: {
        ...reservationInfo, // Dados originais (datas, etc.)
        vehicleChoosedDetails, // O carro que ele já clicou
        vehicleExtras, // Os extras que ele já selecionou
        clientDetails, // Os dados que ele já escreveu no form
        totalRentalValue, // O preço calculado até agora
        pickUpDesk, // Balcões selecionados
        dropOffDesk,
      },
    });
  };

  // Mapeamento dos passos para os Breadcrumbs
  const stepsConfig = [
    { id: "vehicle", label: t("myVehicle") },
    { id: "optionals", label: t("optionals") },
    { id: "clientData", label: t("clientData") },
    { id: "payment", label: t("payment") },
    { id: "confirmation", label: t("confirmation") },
  ];

  // Encontra o índice do passo atual para saber o que mostrar nos breadcrumbs
  const currentStepIndex = stepsConfig.findIndex((s) => s.id === step);
  const activeIndex = currentStepIndex === -1 ? 0 : currentStepIndex;

  return (
    <ThemeProvider theme={theme}>
      <Stack
        spacing={2}
        alignItems={"center"}
        direction="column"
        sx={{ backgroundColor: "primary.main", textAlign: "center", py: 2 }}
      >
        <Breadcrumbs
          maxItems={reservationInfo?.width > 500 ? 8 : 2}
          separator={
            <NavigateNextIcon fontSize="large" sx={{ color: "white" }} />
          }
          aria-label="breadcrumb"
        >
          {stepsConfig.map((item, index) => {
            const isCurrentPage = index === activeIndex;

            return (
              <Typography
                key={item.id}
                variant="h6"
                component="div"
                sx={{
                  textAlign: "center",
                  // Cores: se for a atual usa a cor secundária, se não, fica branco
                  color: isCurrentPage ? "secondary.main" : "white",
                  // Peso da fonte: negrito apenas na atual
                  fontWeight: isCurrentPage ? "bold" : "normal",
                  // Opacidade: a atual fica 100% visível, as outras ficam mais suaves (0.5)
                  opacity: isCurrentPage ? 1 : 0.5,
                  // Removemos interações de clique e ponteiro
                  cursor: "default",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                {item.label}
              </Typography>
            );
          })}
        </Breadcrumbs>
      </Stack>

      <Container sx={{ mt: 4 }}>
        {/* Lógica de Troca de Componentes baseada no URL */}
        {(() => {
          switch (step) {
            case "confirmation":
              return (
                <Confirmation
                  checkInData={checkInData}
                  reservationDetails={reservationDetails}
                  vehicleChoosedDetails={vehicleChoosedDetails}
                  vehicleExtras={vehicleExtras}
                  clientDetails={clientDetails}
                  totalRentalValue={totalRentalValue}
                  pickUpDesk={pickUpDesk}
                  dropOffDesk={dropOffDesk}
                  totalDays={totalDays}
                  paymentType={paymentType}
                />
              );
            case "payment":
              return (
                <Payment
                  vehicleChoosedDetails={vehicleChoosedDetails}
                  reservationInfo={reservationDetails}
                  clientDetails={clientDetails}
                  setPaymentAproved={(val) => {
                    setPaymentAproved(val);
                    if (val) goToStep("confirmation");
                  }}
                  vehicleExtras={vehicleExtras}
                  totalRentalValue={totalRentalValue}
                  paymentAproved={paymentAproved}
                  pickUpDesk={pickUpDesk}
                  dropOffDesk={dropOffDesk}
                  totalDays={totalDays}
                  youngDriverClient={youngDriverClient}
                  setPaymentType={setPaymentType}
                  paymentType={paymentType}
                  setPaypalOrderId={setPaypalOrderId}
                  setTotalRentalValue={setTotalRentalValue}
                  loadingPage={loadingPage}
                />
              );
            case "clientData":
              return (
                <ClientForm
                  reservationInfo={reservationDetails}
                  setWasClientDetailsFiled={() => goToStep("payment")}
                  clientDetails={clientDetails}
                  setClientDetails={setClientDetails}
                  pickUpDesk={pickUpDesk}
                  dropOffDesk={dropOffDesk}
                  setYoungDriverClient={setYoungDriverClient}
                  setVehicleExtras={setVehicleExtras}
                  vehicleExtras={vehicleExtras}
                  youngDriverClient={youngDriverClient}
                />
              );
            case "optionals":
              return (
                <Optionals
                  vehicleChoosedDetails={vehicleChoosedDetails}
                  reservationInfo={reservationDetails}
                  setVehicleExtras={setVehicleExtras}
                  extraFees={vehicleExtras}
                  setWasOptionalsChoosed={() => goToStep("clientData")}
                  setTotalRentalValue={setTotalRentalValue}
                  pickUpDesk={pickUpDesk}
                  dropOffDesk={dropOffDesk}
                  totalRentalValue={totalRentalValue}
                  totalDays={totalDays}
                  wasCarChoosed={true}
                />
              );
            case "vehicle":
            default:
              return (
                <ChooseVehicle
                  setVehicleAvaiabilities={setVehicleAvaiabilities}
                  setReservationDetails={setReservationDetails}
                  reservationInfo={reservationDetails}
                  avaiabilities={avaiabilities}
                  setWasCarChoosed={() => goToStep("optionals")}
                  setVehicleChoosedDetails={setVehicleChoosedDetails}
                  pickUpDesk={pickUpDesk}
                  dropOffDesk={dropOffDesk}
                  setPickUpDesk={setPickUpDesk}
                  setDropOffDesk={setDropOffDesk}
                  totalDays={totalDays}
                  choosedCar={reservationInfo?.choosedCar}
                  wasCarChoosed={false}
                  pageChangedFromOptional={pageChangedFromOptional}
                  setTotalDays={setTotalDays}
                />
              );
          }
        })()}
      </Container>
    </ThemeProvider>
  );
}
export default ReservationCheckout;
